import { useEffect, useState, type FormEvent } from 'react';
import { isApiError } from '../../lib/api';
import {
  createEvalCase,
  deleteEvalCase,
  draftEvalCases,
  getEvalRun,
  listEvalCases,
  listEvalRuns,
  startEvalRun,
  updateEvalCase,
  type EvalCase,
  type EvalMode,
  type EvalResult,
  type EvalRun,
  type ModeMetrics,
} from '../../lib/ai-quality-api';
import { listKnowledge, type KnowledgeEntry } from '../../lib/knowledge-api';
import admin from './admin.module.css';
import styles from './KnowledgeBase.module.css';
import { confirmDialog } from '../../stores/confirm';

/** How often a queued or running evaluation is re-read. */
const RUN_POLL_MS = 2000;
const MODES: EvalMode[] = ['vector', 'keyword'];
const MODE_LABEL: Record<EvalMode, string> = { vector: 'Vector', keyword: 'Keyword' };

const pct = (n: number | null | undefined) => (n == null ? '—' : `${Math.round(n * 100)}%`);
const isLive = (r: EvalRun) => r.status === 'pending' || r.status === 'running';

const ERROR_COPY: Record<string, string> = {
  'nothing-to-check': 'Pick at least one expected entry or phrase before making a question active.',
  'entry-not-in-site': 'One of the chosen entries is no longer in this knowledge base.',
  'no-active-cases': 'Add or activate at least one question first.',
};
function errorCode(err: unknown): string | null {
  return isApiError(err) ? ((err.body as { error?: string } | null)?.error ?? null) : null;
}

function modeSummary(m: ModeMetrics | undefined): string {
  if (!m) return '—';
  if ('unavailable' in m) return m.unavailable === 'no-embeddings-key' ? 'no embeddings key' : m.unavailable;
  return `${pct(m.hitRate)} hit · MRR ${m.mrr == null ? '—' : m.mrr.toFixed(2)}`;
}

// ─── AI evaluation section ─────────────────────────────────────────────────
// The golden-question set: questions customers really ask, each naming the
// knowledge entries (or phrases) a good answer needs. A run retrieves for every
// active question and scores hit rate / MRR per search mode — local and free
// apart from query embeddings; grading answers is optional and spends tokens.
export function RagEvalSection({ siteId }: { siteId: string }) {
  const [cases, setCases] = useState<EvalCase[]>([]);
  const [runs, setRuns] = useState<EvalRun[]>([]);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<EvalCase | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [viewRun, setViewRun] = useState<string | null>(null);
  const [k, setK] = useState(4);
  const [modes, setModes] = useState<EvalMode[]>(['vector', 'keyword']);
  const [grade, setGrade] = useState(false);
  const [starting, setStarting] = useState(false);

  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    try {
      const [c, r, e] = await Promise.all([listEvalCases(siteId), listEvalRuns(siteId), listKnowledge(siteId)]);
      setCases(c);
      setRuns(r);
      setEntries(e);
      setError(null);
    } catch {
      setError('Could not load the evaluation set.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const running = runs.some(isLive);
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      void listEvalRuns(siteId).then(setRuns).catch(() => {});
    }, RUN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [running, siteId]);

  async function draftFromKnowledge() {
    setError(null);
    try {
      const n = await draftEvalCases(siteId);
      setNotice(n === 0 ? 'Every knowledge entry already has a question.' : `Drafted ${n} question${n === 1 ? '' : 's'} — review and activate them.`);
      await load(true);
    } catch {
      setError('Could not draft questions.');
    }
  }

  async function activate(c: EvalCase) {
    setError(null);
    try {
      await updateEvalCase(siteId, c.id, { status: 'active' });
      await load(true);
    } catch (err) {
      setError(ERROR_COPY[errorCode(err) ?? ''] ?? 'Could not activate the question.');
    }
  }

  async function remove(c: EvalCase) {
    if (!(await confirmDialog({ title: `Delete “${c.question}”?` }))) return;
    try {
      await deleteEvalCase(siteId, c.id);
      await load(true);
    } catch {
      setError('Delete failed.');
    }
  }

  async function run() {
    setStarting(true);
    setError(null);
    setNotice(null);
    try {
      await startEvalRun(siteId, { k, modes, gradeAnswers: grade });
      await load(true);
    } catch (err) {
      setError(ERROR_COPY[errorCode(err) ?? ''] ?? 'Could not start the evaluation.');
    } finally {
      setStarting(false);
    }
  }

  const titleOf = (id: string) => entries.find((e) => e.id === id)?.title ?? 'deleted entry';
  const active = cases.filter((c) => c.status === 'active').length;

  return (
    <section className={admin.section}>
      <div className={styles.headRow}>
        <div>
          <p className={admin.kicker}>AI evaluation</p>
          <h2 className={admin.title} style={{ marginBottom: 0 }}>
            Does the AI find the right answer?
          </h2>
        </div>
        <div className={styles.headActions}>
          <button type="button" className={admin.buttonGhost} onClick={() => void draftFromKnowledge()}>
            Draft from knowledge base
          </button>
          <button type="button" className={admin.buttonGhost} onClick={() => setImporting(true)}>
            Import questions
          </button>
          <button type="button" className={admin.button} onClick={() => setEditing('new')}>
            New question →
          </button>
        </div>
      </div>

      <p style={{ marginTop: 0, color: 'var(--inic-ink-subtle)', fontSize: 'var(--inic-size-sm)' }}>
        Golden questions are the ones your customers really ask, each with the knowledge entries or phrases a good
        answer needs. Run them after changing the knowledge base to see whether retrieval still finds the right
        material.
      </p>

      <div className={styles.ragBar}>
        <label className={styles.topkField}>
          <span className={admin.label}>Top-k</span>
          <input
            className={`${admin.input} ${styles.topkInput}`}
            type="number"
            min={1}
            max={20}
            value={k}
            onChange={(e) => setK(Math.max(1, Math.min(20, Math.trunc(Number(e.target.value)) || 1)))}
          />
        </label>
        {MODES.map((m) => (
          <label key={m} className={styles.toggleField}>
            <input
              type="checkbox"
              checked={modes.includes(m)}
              onChange={(e) => setModes(e.target.checked ? [...modes, m] : modes.filter((x) => x !== m))}
            />
            <span className={admin.label} style={{ textTransform: 'none' }}>
              {MODE_LABEL[m]} search
            </span>
          </label>
        ))}
        <label className={styles.toggleField}>
          <input type="checkbox" checked={grade} onChange={(e) => setGrade(e.target.checked)} />
          <span className={styles.toggleCopy}>
            <span className={admin.label} style={{ textTransform: 'none' }}>
              Grade answers
            </span>
            <span className={admin.hint}>Spends AI tokens: two calls per question.</span>
          </span>
        </label>
        <button
          type="button"
          className={admin.button}
          disabled={starting || running || modes.length === 0 || active === 0}
          onClick={() => void run()}
        >
          {running ? 'Running…' : `Run ${active} question${active === 1 ? '' : 's'}`}
        </button>
      </div>

      {error && <p className={admin.error}>{error}</p>}
      {notice && <p className={admin.notice}>{notice}</p>}

      {runs.length > 0 && (
        <>
          <p className={styles.subhead}>Runs</p>
          <div className={admin.tableWrap}>
            <table className={admin.table} aria-label="Evaluation runs">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th>Questions</th>
                  <th>Vector</th>
                  <th>Keyword</th>
                  <th>Answers</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td>{r.status === 'failed' ? `failed: ${r.error ?? ''}` : r.status}</td>
                    <td>{r.status === 'done' ? r.caseCount : '—'}</td>
                    <td>{r.modes.includes('vector') ? modeSummary(r.metrics.modes?.vector) : '—'}</td>
                    <td>{r.modes.includes('keyword') ? modeSummary(r.metrics.modes?.keyword) : '—'}</td>
                    <td>
                      {!r.metrics.grading
                        ? '—'
                        : 'unavailable' in r.metrics.grading
                          ? r.metrics.grading.unavailable
                          : `${r.metrics.grading.avgScore?.toFixed(1) ?? '—'}/5 · ${pct(r.metrics.grading.groundedRate)} grounded`}
                    </td>
                    <td>
                      {r.status === 'done' && (
                        <button type="button" className={admin.linkButton} onClick={() => setViewRun(r.id)}>
                          Results
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <p className={styles.subhead}>Questions</p>
      {loading ? (
        <div className={styles.empty}>Loading…</div>
      ) : cases.length === 0 ? (
        <div className={styles.empty}>
          No questions yet. Draft them from the knowledge base, import the questions from your client audit, or
          write one.
        </div>
      ) : (
        <div className={styles.list}>
          {cases.map((c) => (
            <div key={c.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.rowTitle}>
                  <span className={admin.badge}>{c.status === 'draft' ? 'Draft' : 'Active'}</span>
                  <span className={styles.rowTitleText}>{c.question}</span>
                </span>
                <span className={styles.rowMeta}>
                  {[
                    ...c.expectedEntryIds.map((id) => titleOf(id)),
                    ...c.mustInclude.map((p) => `“${p}”`),
                  ].join(' · ') || 'Nothing to check yet'}
                </span>
              </div>
              <div className={admin.actions}>
                {c.status === 'draft' && (
                  <button type="button" className={admin.buttonGhost} onClick={() => void activate(c)}>
                    Activate
                  </button>
                )}
                <button type="button" className={admin.buttonGhost} onClick={() => setEditing(c)}>
                  Edit
                </button>
                <button type="button" className={admin.buttonDanger} onClick={() => void remove(c)}>
                  delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CaseEditor
          siteId={siteId}
          evalCase={editing === 'new' ? null : editing}
          entries={entries}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}
      {importing && (
        <ImportDialog
          siteId={siteId}
          onClose={() => setImporting(false)}
          onImported={(n) => {
            setImporting(false);
            setNotice(`Imported ${n} question${n === 1 ? '' : 's'} as drafts — add what each should find, then activate.`);
            void load(true);
          }}
        />
      )}
      {viewRun && <RunResults siteId={siteId} runId={viewRun} titleOf={titleOf} onClose={() => setViewRun(null)} />}
    </section>
  );
}

// ─── New / edit question modal ─────────────────────────────────────────────
function CaseEditor({
  siteId,
  evalCase,
  entries,
  onClose,
  onSaved,
}: {
  siteId: string;
  evalCase: EvalCase | null;
  entries: KnowledgeEntry[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [question, setQuestion] = useState(evalCase?.question ?? '');
  const [expected, setExpected] = useState<string[]>(evalCase?.expectedEntryIds ?? []);
  const [phrases, setPhrases] = useState((evalCase?.mustInclude ?? []).join('\n'));
  const [status, setStatus] = useState<'active' | 'draft'>(evalCase?.status ?? 'active');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!question.trim()) return;
    setBusy(true);
    setError(null);
    const input = {
      question: question.trim(),
      expectedEntryIds: expected,
      mustInclude: phrases.split('\n').map((p) => p.trim()).filter(Boolean),
      status,
    };
    try {
      if (evalCase) await updateEvalCase(siteId, evalCase.id, input);
      else await createEvalCase(siteId, input);
      onSaved();
    } catch (err) {
      setError(ERROR_COPY[errorCode(err) ?? ''] ?? 'Could not save the question.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={admin.modalBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={evalCase ? 'Edit question' : 'New question'}
      onClick={onClose}
    >
      <form
        className={admin.modal}
        style={{ maxWidth: 640, maxHeight: '88vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 className={admin.modalTitle}>{evalCase ? 'Edit question' : 'New question'}</h2>
        <label className={admin.field}>
          <span className={admin.label}>Question</span>
          <input
            className={admin.input}
            value={question}
            maxLength={1000}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="How long does a refund take?"
            autoFocus
          />
        </label>
        <fieldset className={admin.field} style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className={admin.label}>Entries a good answer needs</legend>
          <div style={{ maxHeight: 200, overflowY: 'auto', display: 'grid', gap: 4 }}>
            {entries.length === 0 && <span className={admin.hint}>The knowledge base is empty.</span>}
            {entries.map((entry) => (
              <label key={entry.id} className={styles.toggleField}>
                <input
                  type="checkbox"
                  checked={expected.includes(entry.id)}
                  onChange={(e) =>
                    setExpected(e.target.checked ? [...expected, entry.id] : expected.filter((id) => id !== entry.id))
                  }
                />
                <span>{entry.title}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label className={admin.field}>
          <span className={admin.label}>Phrases the retrieved text must contain — one per line</span>
          <textarea className={admin.input} rows={3} value={phrases} onChange={(e) => setPhrases(e.target.value)} placeholder="30 days" />
        </label>
        <label className={styles.toggleField}>
          <input type="checkbox" checked={status === 'draft'} onChange={(e) => setStatus(e.target.checked ? 'draft' : 'active')} />
          <span className={admin.label} style={{ textTransform: 'none' }}>
            Keep as draft (not run)
          </span>
        </label>
        {error && <p className={admin.error}>{error}</p>}
        <div className={admin.modalActions}>
          <button type="button" className={admin.buttonGhost} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={admin.button} disabled={busy || !question.trim()}>
            {busy ? 'Saving…' : 'Save question'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Import questions (e.g. from a client audit) as drafts ─────────────────
function ImportDialog({ siteId, onClose, onImported }: { siteId: string; onClose: () => void; onImported: (n: number) => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const questions = [...new Set(text.split('\n').map((q) => q.trim()).filter(Boolean))].slice(0, 200);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (questions.length === 0) return;
    setBusy(true);
    setError(null);
    let done = 0;
    try {
      for (const question of questions) {
        await createEvalCase(siteId, { question: question.slice(0, 1000), status: 'draft' });
        done += 1;
      }
      onImported(done);
    } catch {
      setError(`Imported ${done} of ${questions.length}; the rest failed.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={admin.modalBackdrop} role="dialog" aria-modal="true" aria-label="Import questions" onClick={onClose}>
      <form className={admin.modal} style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2 className={admin.modalTitle}>Import questions</h2>
        <label className={admin.field}>
          <span className={admin.label}>One question per line</span>
          <textarea
            className={admin.input}
            rows={10}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={'Do you ship to Canada?\nHow do I reset my password?'}
            autoFocus
          />
          <span className={admin.hint}>Each becomes a draft. Up to 200 at a time.</span>
        </label>
        {error && <p className={admin.error}>{error}</p>}
        <div className={admin.modalActions}>
          <button type="button" className={admin.buttonGhost} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={admin.button} disabled={busy || questions.length === 0}>
            {busy ? 'Importing…' : `Import ${questions.length}`}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── One run's per-question results ────────────────────────────────────────
function RunResults({
  siteId,
  runId,
  titleOf,
  onClose,
}: {
  siteId: string;
  runId: string;
  titleOf: (id: string) => string;
  onClose: () => void;
}) {
  const [results, setResults] = useState<EvalResult[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getEvalRun(siteId, runId)
      .then((d) => setResults(d.results))
      .catch(() => setError(true));
  }, [siteId, runId]);

  return (
    <div className={admin.modalBackdrop} role="dialog" aria-modal="true" aria-label="Run results" onClick={onClose}>
      <div className={admin.modal} style={{ maxWidth: 900, maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <h2 className={admin.modalTitle}>Run results</h2>
        {error && <p className={admin.error}>Could not load the results.</p>}
        {!results && !error && <p className={admin.hint}>Loading…</p>}
        {results && (
          <div className={admin.tableWrap}>
            <table className={admin.table}>
              <thead>
                <tr>
                  <th>Question</th>
                  <th>Mode</th>
                  <th>Result</th>
                  <th>Retrieved</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={`${r.caseId}:${r.mode}`}>
                    <td>
                      {r.question}
                      {r.grade && (
                        <div className={admin.hint}>
                          Answer {r.grade.score}/5{r.grade.grounded ? ', grounded' : ', not grounded'} — {r.grade.reason}
                        </div>
                      )}
                    </td>
                    <td>{MODE_LABEL[r.mode]}</td>
                    <td>{r.hit ? (r.rank ? `Pass · rank ${r.rank}` : 'Pass') : 'Miss'}</td>
                    <td>{r.retrievedEntryIds.map(titleOf).join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className={admin.modalActions}>
          <button type="button" className={admin.button} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
